import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button } from 'react-bootstrap';
import { debounce } from 'lodash';

// This component is extracted from FilmList.js
// It provides a generic filter UI (text input or select) for a given column.

function Filter({ column }) {
  const { filterVariant, filterOptions } = column.columnDef.meta || {};
  const columnFilterValue = column.getFilterValue();
  const { setFilterValue } = column;

  // --- Hooks for all filter types ---
  
  // State for text input
  const [value, setValue] = useState(columnFilterValue ?? '');
  
  // State for multi-select
  const initialSelection = useMemo(() => {
    if (columnFilterValue === null) return ['N/A'];
    return Array.isArray(columnFilterValue) ? columnFilterValue.map(String) : [];
  }, [columnFilterValue]);

  const [draftSelection, setDraftSelection] = useState(initialSelection);

  useEffect(() => {
    setDraftSelection(initialSelection);
  }, [initialSelection]);

  // Modalita' avanzata (contiene/non contiene) per la variante 'select': il valore
  // applicato e' un oggetto sentinella { __advanced, text } invece dell'array di
  // selezione, riconosciuto da chi serializza i filtri per l'API (vedi FilmList.js,
  // mappa su operatori ilike/not_ilike/isnot).
  const appliedAdvanced = (columnFilterValue && typeof columnFilterValue === 'object' && !Array.isArray(columnFilterValue) && columnFilterValue.__advanced)
    ? columnFilterValue
    : null;
  const initialAdvancedMode = (appliedAdvanced?.__advanced === 'contains' || appliedAdvanced?.__advanced === 'not_contains')
    ? appliedAdvanced.__advanced
    : null;
  const [searchTerm, setSearchTerm] = useState('');
  const [advancedMode, setAdvancedMode] = useState(initialAdvancedMode); // 'contains' | 'not_contains' | null
  const [advancedText, setAdvancedText] = useState(appliedAdvanced?.text || '');

  // Debounce for text input
  const debouncedSetFilterValue = useMemo(
    () => debounce(setFilterValue, 1000),
    [setFilterValue]
  );

  useEffect(() => {
    return () => {
      debouncedSetFilterValue.cancel();
    };
  }, [debouncedSetFilterValue]);

  // --- End Hooks ---

  // Unique values for dropdowns, sorted numerically if possible
  const sortedUniqueValues = useMemo(
    () => {
      let options = filterOptions || [];
      if (options.length > 0 && typeof options[0] === 'object' && options[0] !== null && 'label' in options[0]) {
        return [...options].sort((a, b) => String(a.label).localeCompare(String(b.label)));
      }
      return [...options].filter(o => o !== null && o !== undefined).sort((a, b) => {
        const numA = Number(a);
        const numB = Number(b);
        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
        }
        return String(a).localeCompare(String(b));
      });
    },
    [filterOptions]
  );
  
  const handleChange = (e) => {
    setValue(e.target.value);
    debouncedSetFilterValue(e.target.value);
  };

  // --- Multi-select checkbox logic for all 'select' variants ---
  if (filterVariant === 'select') {
    const handleMultiSelectChange = (selectedValue, isChecked) => {
        setAdvancedMode(null);
        const sValue = String(selectedValue);
        setDraftSelection(prev => {
            if (sValue === 'N/A') {
                return isChecked ? ['N/A'] : [];
            }
            const newSelection = isChecked
                ? [...prev.filter(v => v !== 'N/A'), sValue]
                : prev.filter(v => v !== sValue && v !== 'N/A');
            return newSelection;
        });
    };

    const handleApply = () => {
        if (advancedMode && advancedText.trim()) {
            setFilterValue({ __advanced: advancedMode, text: advancedText.trim() });
        } else if (draftSelection.includes('N/A')) {
            setFilterValue(null);
        } else {
            // Convert numbers back to numbers before sending
            const finalSelection = draftSelection.map(v => {
                const num = Number(v);
                return isNaN(num) ? v : num;
            });
            setFilterValue(finalSelection.length > 0 ? finalSelection : undefined);
        }
    };

    const handleClear = () => {
        setDraftSelection([]);
        setAdvancedMode(null);
        setAdvancedText('');
        setFilterValue(undefined);
    };

    const handleNonVuoti = () => {
        setAdvancedMode(null);
        setDraftSelection([]);
        setFilterValue({ __advanced: 'not_empty' });
    };

    const isNASelected = draftSelection.includes('N/A');
    const valoriVisibili = searchTerm.trim()
      ? sortedUniqueValues.filter(opt => {
          const label = typeof opt === 'object' && opt !== null && 'label' in opt ? opt.label : opt;
          return String(label).toLowerCase().includes(searchTerm.trim().toLowerCase());
        })
      : sortedUniqueValues;

    return (
      <div className="p-2" style={{ minWidth: '220px' }}>
        <div className="d-flex flex-column gap-1 mb-2">
          <Button variant="link" size="sm" className="text-start p-0" onClick={handleClear}>Tutti</Button>
          <Button variant="link" size="sm" className="text-start p-0" onClick={handleNonVuoti}>Voci non vuote</Button>
          <Button
            variant="link" size="sm" className="text-start p-0"
            onClick={() => setAdvancedMode(prev => prev === 'contains' ? null : 'contains')}
          >
            Contenente...
          </Button>
          <Button
            variant="link" size="sm" className="text-start p-0"
            onClick={() => setAdvancedMode(prev => prev === 'not_contains' ? null : 'not_contains')}
          >
            Non contenente...
          </Button>
        </div>
        {advancedMode ? (
          <Form.Control
            type="text"
            size="sm"
            autoFocus
            placeholder="Testo da cercare..."
            value={advancedText}
            onChange={e => setAdvancedText(e.target.value)}
          />
        ) : (
          <>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Cerca nella lista..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="mb-2"
            />
            <Form.Check
              key="na-filter"
              type="checkbox"
              label="N/A (Non Assegnato)"
              checked={isNASelected}
              onChange={e => handleMultiSelectChange('N/A', e.target.checked)}
            />
            <hr className="my-2" />
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {valoriVisibili.map(opt => {
                  const val = typeof opt === 'object' && opt !== null && 'value' in opt ? opt.value : opt;
                  const label = typeof opt === 'object' && opt !== null && 'label' in opt ? opt.label : opt;
                  const sVal = String(val);

                  return (
                    <Form.Check
                      key={sVal}
                      type="checkbox"
                      label={String(label)}
                      checked={!isNASelected && draftSelection.includes(sVal)}
                      disabled={isNASelected}
                      onChange={e => handleMultiSelectChange(sVal, e.target.checked)}
                    />
                  )
                })}
                {valoriVisibili.length === 0 && (
                  <div className="text-muted small">Nessun valore corrisponde alla ricerca.</div>
                )}
            </div>
          </>
        )}
        <hr className="my-2" />
        <div className="d-flex justify-content-between">
            <Button variant="primary" size="sm" onClick={handleApply}>
              Applica
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={handleClear}>
              Azzera
            </Button>
        </div>
      </div>
    );
  }

  // Range di date o di numeri: valore applicato { from, to }, entrambi opzionali.
  // Azzerato a undefined (non {}) quando entrambi i capi sono vuoti, altrimenti
  // column.getIsFiltered() risulterebbe true anche a filtro svuotato.
  if (filterVariant === 'date-range' || filterVariant === 'number-range') {
    const range = (columnFilterValue && typeof columnFilterValue === 'object' && !Array.isArray(columnFilterValue)) ? columnFilterValue : {};
    const isNumber = filterVariant === 'number-range';

    const updateRange = (patch) => {
      const next = { ...range, ...patch };
      const isEmpty = (next.from === undefined || next.from === '') && (next.to === undefined || next.to === '');
      setFilterValue(isEmpty ? undefined : next);
    };

    const parse = (raw) => {
      if (raw === '') return undefined;
      return isNumber ? (raw === '' ? undefined : Number(raw)) : raw;
    };

    return (
      <div className="p-2 d-flex flex-column gap-2" style={{ minWidth: '180px' }}>
        <Form.Group>
          <Form.Label className="small mb-1">Da</Form.Label>
          <Form.Control
            type={isNumber ? 'number' : 'date'}
            size="sm"
            value={range.from ?? ''}
            onChange={e => updateRange({ from: parse(e.target.value) })}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className="small mb-1">A</Form.Label>
          <Form.Control
            type={isNumber ? 'number' : 'date'}
            size="sm"
            value={range.to ?? ''}
            onChange={e => updateRange({ to: parse(e.target.value) })}
          />
        </Form.Group>
        {(range.from !== undefined || range.to !== undefined) && (
          <Button variant="outline-secondary" size="sm" onClick={() => setFilterValue(undefined)}>Azzera</Button>
        )}
      </div>
    );
  }

  // Render for text input (default)
  return (
    <Form.Control
      type="text"
      placeholder={`Filtra...`}
      value={value}
      onChange={handleChange}
      className="mt-1"
    />
  );
}

export default Filter;
